import React, { useContext, useEffect, useState } from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Modal,
    Paper,
    Tab,
    Tabs,
} from '@material-ui/core';

import { logisticsApi } from 'api/logistics';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FormComponent, Notifications, TabPanel } from 'shared';

function CartExperience() {
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const user = useContext(UserContext);

    const getItemsInTable = async () => {
        const response = await logisticsApi.getCartExperiences();

        if (response.status === false) {
            setNotification({ text: `No cart experiences found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        const cxDetails = response.data.cartExperiences;
        setRows(cxDetails);
    };
    useEffect(() => {
        getItemsInTable();
    }, []);

    const canChangeCartExperience = user.canChangeCartExperience();

    const columns = [
        { accessor: 'id', Header: 'ID', editable: false },
        { accessor: 'zipCode', Header: 'CX - Zip Code Override', editable: false },
        {
            accessor: 'freeShippingEligible',
            Header: 'CX - Free Shipping Eligible',
            editable: canChangeCartExperience,
            type: 'checkbox',
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'freeShippingThreshold',
            Header: 'CX - Free Shipping Threshold (USD Cents)',
            editable: canChangeCartExperience,
            editProps: { type: 'input', inputType: 'number' },
        },
        {
            accessor: 'orderMinimumOverridden',
            Header: 'CX - Order Minimum Overridden',
            editable: canChangeCartExperience,
            type: 'checkbox',
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'orderMinimum',
            Header: 'CX - Order Minimum (USD Cents)',
            editable: canChangeCartExperience,
            editProps: { type: 'input', inputType: 'number' },
        },
        {
            accessor: 'coldKeeperFeeEnabled',
            Header: 'CX - Cold Keeper Fee Enabled',
            editable: canChangeCartExperience,
            type: 'checkbox',
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'coldKeeperFee',
            Header: 'CX - Cold Keeper Fee (USD Cents)',
            editable: canChangeCartExperience,
            editProps: { type: 'input', inputType: 'number' },
        },
        {
            accessor: 'frozenFeeEnabled',
            Header: 'CX - Frozen Fee Enabled',
            editable: canChangeCartExperience,
            type: 'checkbox',
            editProps: { type: 'checkbox' },
        },
        {
            accessor: 'frozenFee',
            Header: 'CX - Frozen Fee (USD Cents)',
            editable: canChangeCartExperience,
            editProps: { type: 'input', inputType: 'number' },
        },
    ];

    // Custom export function to ensure cart experience fields are properly prefixed
    const customCartExperienceExport = (rows) => {
        // Define the cart experience fields that need the cartExperience prefix
        // These are all fields with "CX -" headers (cart experience related)
        const cartExperienceFields = [
            'zipCode',
            'freeShippingEligible',
            'freeShippingThreshold',
            'orderMinimumOverridden',
            'orderMinimum',
            'coldKeeperFeeEnabled',
            'coldKeeperFee',
            'frozenFeeEnabled',
            'frozenFee',
        ];

        // Create headers with cartExperience prefix for the relevant fields
        const headers = columns
            .filter((col) => col.accessor !== 'id') // Exclude ID from export
            .map((col) => {
                if (cartExperienceFields.includes(col.accessor)) {
                    return `cartExperience.${col.accessor}`;
                }
                return col.accessor;
            });

        const headerContent = headers.join(',');

        let csvContent = `${headerContent}\r\n`;

        for (const row of rows) {
            const rowData = [];

            columns
                .filter((col) => col.accessor !== 'id') // Exclude ID from export
                .forEach((col) => {
                    const cellContent = row.original[col.accessor];
                    if (cellContent === null || cellContent === undefined) {
                        rowData.push('');
                    } else {
                        rowData.push(`"${cellContent}"`);
                    }
                });

            csvContent += `${rowData.join(',')}\r\n`;
        }

        // Use the same download logic as the DataTable component
        const downloadCSV = (content, filename) => {
            const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        const today = new Date();
        const timestamp = today.toISOString().slice(0, 19).replace(/[-:]/g, '_').replace('T', '_');
        const filename = `cart-experience_${timestamp}.csv`;

        downloadCSV(csvContent, filename);
    };

    return (
        <Grid container>
            <Notifications options={notification} />
            <div style={{ marginBottom: '2em', textAlign: 'left' }}>
                <small>
                    Note: You can submit a file exported from the table below. The export will include fields prefixed
                    with <mono>cartExperience.</mono> for the updatable cart experience fields. Only these prefixed
                    columns will be processed during import.
                </small>
                <FormComponent
                    formFields={[
                        {
                            name: 'cartExperienceFile',
                            label: 'Upload Cart Experience',
                            inputElement: 'file',
                            accept: ['text/csv'],
                        },
                    ]}
                    onSubmit={async (formData, resetForm) => {
                        const result = await logisticsApi.uploadCartExperience(formData);
                        setNotification({ text: result.msg, level: result.status ? 'success' : 'error' });
                        if (result.status) {
                            resetForm();
                        }
                        getItemsInTable();
                    }}
                />
            </div>
            <DataTable
                editable
                title="Zip Codes"
                columns={columns}
                data={rows}
                csvExport
                csvExportFileName="cart-experience"
                customExport={customCartExperienceExport}
            />
        </Grid>
    );
}

export { CartExperience };
